import moment from 'moment-timezone'

export const formatDate = (date) => {
  return moment(date).format('MM/DD/YYYY')
}

export const formatYear = (year) => {
  return moment(year).format('YYYY')
}

export const UTCFormatDate = (date) => {
  return moment.utc(date).format('MM/DD/YYYY')
}

/** Experience list dates — e.g. "22 Aug 2027" (v7 HTML). */
export const UTCFormatDateShort = (date) => {
  if (date == null) {
    return null
  }
  return moment.utc(date).format('D MMM YYYY')
}

/** View-form dates — e.g. "12 July 2026" (v7 enrollment response). */
export const formatExperienceDate = (date) => {
  if (date == null || date === '') return null
  const asString = String(date).trim()
  const monthDayYear = moment(asString, 'MM/DD/YYYY', true)
  if (monthDayYear.isValid()) return monthDayYear.format('D MMMM YYYY')
  const isoDay = moment.utc(asString, 'YYYY-MM-DD', true)
  if (isoDay.isValid()) return isoDay.format('D MMMM YYYY')
  const utc = moment.utc(asString)
  if (utc.isValid()) return utc.format('D MMMM YYYY')
  return null
}

export const formatDateInPlan = (date) => {
  return moment(date).format('YYYY-MM-DD')
}

export const UTCformatDateInPlan = (date) => {
  return moment.utc(date).format('YYYY-MM-DD')
}

export const formatDateInDetail = (date) => {
  return moment(date).format('MM-DD-YYYY')
}

export const UTCFormatItineary = (date) => {
  return moment.utc(date).format('MM-DD-YYYY')
}

export const convertSessionDate = (date, format = 'MM/DD/YYYY') => {
  return date ? moment(date).utcOffset('+0000').format(format) : null
}

export const currentYearFormat = () => {
  return moment().year()
}

export const getCurrentDate = () => moment()

export const getcurrentDateUsingProps = (date) => {
  return moment(date)
}

export const getUTCcurrentDateUsingProps = (date) => {
  return moment.utc(date)
}

export const getItineraryDate = (date) =>
  moment.utc(date).format('MMMM D, YYYY')

/** Itinerary editor kicker — e.g. "SUNDAY, 17 JANUARY 2027" (v7 HTML). */
export const getItineraryLongDate = (date) =>
  moment.utc(date).format('dddd, D MMMM YYYY').toUpperCase()

/** Itinerary day-nav subline date — e.g. "Sun 17 Jan". */
export const getItineraryNavDate = (date) =>
  moment.utc(date).format('ddd D MMM')

/** Timed entry length label — e.g. "1h 15m", "45m", or "—". */
export const getItineraryTimeLength = (fromTime, toTime) => {
  if (!fromTime || !toTime) return '—'
  const [fh, fm] = String(fromTime).split(':').map(Number)
  const [th, tm] = String(toTime).split(':').map(Number)
  if ([fh, fm, th, tm].some((n) => Number.isNaN(n))) return '—'
  const mins = th * 60 + tm - (fh * 60 + fm)
  if (mins <= 0) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

export const getIntentionDate = (date) => moment(date).format('MMMM Do YYYY')

export const getArchivedIntentionDate = (date) =>
  moment(date).format('Do MMMM YYYY')

/**
 * Resolve start/end from the first programDatesList entry (index 0).
 * Falls back to legacy startDate/endDate on the quest.
 */
export const getProgramDatesRange = (quest) => {
  const firstDate = quest?.programDatesList?.[0]
  if (firstDate?.startDate || firstDate?.endDate) {
    return {
      start: firstDate.startDate || null,
      end: firstDate.endDate || firstDate.startDate || null,
    }
  }
  return {
    start: quest?.startDate || null,
    end: quest?.endDate || null,
  }
}

export const sortedByDate = (data) => {
  return data?.sort((a, b) => {
    const { start: startA, end: endA } = getProgramDatesRange(a)
    const { start: startB, end: endB } = getProgramDatesRange(b)
    const startDateA = moment(startA)
    const startDateB = moment(startB)

    if (startDateA > startDateB) {
      return 1
    } else if (startDateA < startDateB) {
      return -1
    } else {
      const endDateA = moment(endA)
      const endDateB = moment(endB)
      if (endDateA > endDateB) {
        return 1
      } else if (endDateA < endDateB) {
        return -1
      } else {
        return 0
      }
    }
  })
}

/**
 * Format date and time with timezone support
 * Uses moment-timezone for accurate DST handling across all dates and timezones
 * @param {string|Date} dateTime - ISO date string or Date object
 * @param {string} timezone - IANA timezone (e.g., 'America/New_York', 'Asia/Kolkata', 'America/Vancouver')
 * @returns {object} Object with date and time formatted strings
 */
export const formatDateTimeWithTimezone = (dateTime, timezone) => {
  if (!dateTime) return { date: '-', time: '-' }

  try {
    const momentDate = moment(dateTime).tz(timezone || moment.tz.guess())

    if (!momentDate.isValid()) {
      return { date: '-', time: '-' }
    }

    const formattedDate = momentDate.format('MM/DD/YYYY')
    const formattedTime = momentDate.format('h:mmA')

    return { date: formattedDate, time: formattedTime }
  } catch (error) {
    console.error('Error formatting date/time:', error)
    return { date: '-', time: '-' }
  }
}

export const getMomentDate = (date, format = null) => {
  if (!date) return null
  return format ? moment(date, format, true) : moment(date)
}

export const isValidDate = (dateString, format = null) => {
  if (!dateString) return false
  const date = format ? moment(dateString, format, true) : moment(dateString)
  return date.isValid()
}

export const isDateBefore = (date1, date2, format = null) => {
  if (!date1 || !date2) return false
  const d1 = format ? moment(date1, format, true) : moment(date1)
  const d2 = format ? moment(date2, format, true) : moment(date2)
  return d1.isValid() && d2.isValid() && d1.isBefore(d2)
}

export const isDateAfter = (date1, date2, format = null) => {
  if (!date1 || !date2) return false
  const d1 = format ? moment(date1, format, true) : moment(date1)
  const d2 = format ? moment(date2, format, true) : moment(date2)
  return d1.isValid() && d2.isValid() && d1.isAfter(d2)
}

export const getDateWithOffset = (amount, unit = 'years', subtract = false) => {
  const date = moment()
  return subtract ? date.subtract(amount, unit) : date.add(amount, unit)
}

export const formatDateForDisplay = (dateString) => {
  if (!dateString) return ''

  // If it's already in MM/DD/YYYY format, return as is
  if (moment(dateString, 'MM/DD/YYYY', true).isValid()) {
    return dateString
  }

  // If it's an ISO string, parse and format to MM/DD/YYYY
  const date = moment(dateString)
  if (date.isValid()) {
    return date.format('MM/DD/YYYY')
  }

  return dateString
}

export const dateToISOString = (dateString, format = 'MM/DD/YYYY') => {
  if (!dateString) return null
  const date = moment(dateString, format, true)
  return date.isValid() ? date.toISOString() : null
}

export const formatDateLong = (date) => {
  if (!date) return ''
  return moment(date).format('MMMM D, YYYY')
}

export const formatDateWithOrdinal = (date) => {
  if (!date) return ''
  return moment(date).format('Do MMMM, YYYY')
}

export const formatDateMedium = (date) => {
  if (!date) return ''
  return moment(date).format('MMM DD, YYYY')
}

export const formatDateTimeFull = (date) => {
  if (!date) return ''
  return moment(date).format('ddd , MM/DD/YYYY , hh:mm:ss A')
}

export const isDateBetween = (date, startDate, endDate, format = null) => {
  if (!date || !startDate || !endDate) return false
  const d = format ? moment(date, format, true) : moment(date)
  const start = format ? moment(startDate, format, true) : moment(startDate)
  const end = format ? moment(endDate, format, true) : moment(endDate)
  return (
    d.isValid() &&
    start.isValid() &&
    end.isValid() &&
    d.isBetween(start, end, null, '[]')
  )
}

export const isDateAfterNow = (date, format = null) => {
  if (!date) return false
  const d = format ? moment(date, format, true) : moment(date)
  return d.isValid() && d.isAfter(moment())
}
